// JAPAN WEB CLINIC (Dawn) — the cart page (plan §3⑤, acceptance C01–C12):
// - the receiving summary, the stay dates to confirm (okurun revision 11: 「予約と同じです」), the carry check, the
//   contact app (saved at once, never chosen for the customer), the consents (unchecked at first; a box counts only for
//   the same text version and the same content it covered)
// - the checkout buttons (page and phone bar) stay closed until all of that is in place, and while Dawn redraws the
//   cart after a quantity change; after each redraw everything is checked again (C02)
// - the certificate is one line, quantity 1 (okurun §5); without medicine the certificate and cooler bag leave (C04)
// - checkout: the consent record and the contact app are saved on the cart first, the checkout's delivery method and
//   prefecture are set from the receiving details (C09), then Dawn's cart form goes to checkout.
(() => {
  'use strict';
  const J = window.JWC;
  const root = document.querySelector('[data-jwc-cart]');
  const cfgEl = document.querySelector('[data-jwc-cart-data]');
  const dataEl = document.querySelector('[data-jwc-receive-data]');
  if (!J || !cfgEl || !dataEl) return;
  const cfg = JSON.parse(cfgEl.textContent);
  const D = JSON.parse(dataEl.textContent);
  const T = { ...J.texts(D.T), ...J.texts(cfg.T) };
  const $ = (sel, el = document) => el.querySelector(sel);
  const $$ = (sel, el = document) => Array.from(el.querySelectorAll(sel));
  const routes = window.routes || {};
  const cartUrl = (routes.cart_url || '/cart').replace(/\/$/, '');
  const attrRoutes = { cart: cartUrl, cartUpdate: (routes.cart_update_url || '/cart/update').replace(/\.js$/, '') };
  const settings = J.settings || {};
  const schedule = () => J.makeSchedule(settings.schedule || {});
  const rules = D.carryRules?.countries || {};
  const certId = D.certificate?.variantId;
  const coolerIds = (D.cooler || []).map((c) => c.variantId);
  const CONSENTS = ['terms_privacy', 'prescription_refund', 'carry_guide'];
  const LEGACY = { terms_privacy: '利用規約・プライバシー', prescription_refund: '処方の条件・不可なら全額返金', carry_guide: '持ち帰りガイド確認' };
  const DRAFT_KEY = 'jwc-consent-draft';
  const DATES_KEY = 'jwc-stay-dates';
  const queue = J.serialQueue();
  let cart = null;
  let digests = {};
  let checked = {};
  let updating = false;
  let leaving = false;
  let pendingApp = null; // the app being saved (shown as chosen until the save ends)
  let tried = false; // the blocked button was tapped: what is still missing shows in red (owner 2026-10-20)

  // ---------------------------------------------------------------- derived
  const isCert = (l) => l.variant_id === certId;
  const isCooler = (l) => coolerIds.includes(l.variant_id);
  const medicines = () => (cart?.items || []).filter((l) => !isCert(l) && !isCooler(l));
  const receipt = () => J.receiptFromAttributes(cart?.attributes);
  const abroad = (r) => Boolean(r && r.destination && r.destination !== 'jp');
  const required = (r) => (abroad(r) ? CONSENTS : CONSENTS.filter((c) => c !== 'carry_guide'));
  const needsDates = (r) => Boolean(r && r.method === 'delivery' && r.stay === 'traveling' && J.validDate(r.checkin) && J.validDate(r.checkout));
  const datesKey = (r) => (needsDates(r) ? `${r.checkin}|${r.checkout}` : '');
  function known() {
    const m = new Map();
    Object.entries(D.cartVariants || {}).forEach(([id, v]) => m.set(Number(id), { group: v.group || null, pens: v.pens || null }));
    if (certId) m.set(certId, { group: null, pens: 0 });
    coolerIds.forEach((id) => m.set(id, { group: null, pens: 0 }));
    return m;
  }
  function pens() {
    const group = Object.values(D.cartVariants || {}).find((v) => v.group)?.group || null;
    return J.cartPens(cart?.items || [], known(), group);
  }
  const receiptCtx = () => ({ schedule: schedule(), T, pickupEnabled: D.pickupEnabled, deliverySlots: D.deliverySlots, pickupSlots: D.pickupSlots });
  const receiptProblem = (r) => (r ? J.receiptIssue(r, receiptCtx()) || J.returnIssue(schedule(), r, T) : { message: T.receipt_missing });

  async function computeDigests(r) {
    const lines = medicines()
      .map((l) => ({ variant_id: l.variant_id, quantity: l.quantity, properties: l.properties || {} }))
      .sort((a, b) => a.variant_id - b.variant_id);
    const receiptAttrs = Object.fromEntries(J.RECEIPT_KEYS.map((k) => [k, cart?.attributes?.[k] ?? '']));
    const extras = (cart?.items || []).filter((l) => isCert(l) || isCooler(l)).map((l) => ({ variant_id: l.variant_id, quantity: l.quantity })).sort((a, b) => a.variant_id - b.variant_id);
    const p = pens();
    const contexts = {
      prescription_refund: { lines, optional: extras, receipt: receiptAttrs },
      carry_guide: { destination: r?.destination || '', total: p.unknown ? null : p.total, rules_revision: D.carryRules?.revision || '' },
    };
    const out = { terms_privacy: 'v' };
    for (const [id, context] of Object.entries(contexts)) out[id] = await J.sha256(J.stableJson(context));
    return out;
  }

  // ---------------------------------------------------------------- consents: saved on the cart, or this tab's draft
  // The draft keeps each box the guest touched in this tab, ticked (the value it covered) or unticked (''): the latest
  // touch wins over a consent saved on the cart at an earlier checkout (JWC-07: an untick came back after a quantity
  // change or a reload)
  function readDraft() {
    try {
      const d = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || 'null');
      return d && d.token === cart?.token ? d.checked || {} : {};
    } catch (e) {
      return {};
    }
  }
  function restoreConsents() {
    let saved = null;
    try {
      saved = JSON.parse(cart?.attributes?.jwc_consent_receipt_v1 || 'null');
    } catch (e) {
      saved = null;
    }
    const draft = readDraft();
    checked = {};
    CONSENTS.forEach((id) => {
      const item = saved?.items?.find((x) => x.id === id);
      const digest = id === 'terms_privacy' ? cfg.consentVersions[id] : `${cfg.consentVersions[id]}:${digests[id]}`;
      if (Object.hasOwn(draft, id)) {
        if (draft[id] === digest) checked[id] = digest;
      } else if (item && item.version === cfg.consentVersions[id] && (id === 'terms_privacy' || item.relevant_context_digest === digests[id])) checked[id] = digest;
    });
  }
  function writeDraft(id, value) {
    try {
      const draft = readDraft();
      draft[id] = value;
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ token: cart?.token, checked: draft }));
    } catch (e) {
      /* the boxes start from the saved consent next time */
    }
  }
  const consentValue = (id) => (id === 'terms_privacy' ? cfg.consentVersions[id] : `${cfg.consentVersions[id]}:${digests[id]}`);
  const datesConfirmed = (r) => {
    try {
      return Boolean(datesKey(r)) && sessionStorage.getItem(DATES_KEY) === `${cart?.token}|${datesKey(r)}`;
    } catch (e) {
      return false;
    }
  };

  // ---------------------------------------------------------------- what is still missing (C01, okurun §4-3)
  function blocker() {
    if (updating) return { step: 'consents', message: T.reminder_updating };
    if (!medicines().length) return { step: 'receipt', message: T.reminder_empty };
    const r = receipt();
    const problem = receiptProblem(r);
    if (problem) return { step: 'receipt', message: r ? problem.message : T.reminder_receipt };
    if (needsDates(r) && !datesConfirmed(r)) return { step: 'receipt', message: T.reminder_dates };
    if (!Object.hasOwn(rules, r.destination)) return { step: 'carry', message: T.reminder_carry };
    if (!cart.attributes?.['連絡アプリ']) return { step: 'contact', message: T.reminder_contact };
    const need = required(r);
    if (need.some((id) => checked[id] !== consentValue(id))) return { step: 'consents', message: need.length === 3 ? T.reminder_consents_3 : T.reminder_consents_2 };
    return null;
  }
  function leftCount() {
    if (!cart) return 0;
    const r = receipt();
    let n = 0;
    if (!r || receiptProblem(r)) n++;
    if (r && needsDates(r) && !datesConfirmed(r)) n++;
    if (!r || !Object.hasOwn(rules, r.destination)) n++;
    if (!cart.attributes?.['連絡アプリ']) n++;
    n += required(r).filter((id) => checked[id] !== consentValue(id)).length;
    return n;
  }

  // ---------------------------------------------------------------- render
  const setText = (sel, text, el = document) => $$(sel, el).forEach((x) => (x.textContent = text));
  function render() {
    if (!cart) return;
    const r = receipt();
    const problem = receiptProblem(r);
    // receiving summary
    if (r) {
      const v = J.receiptValues(r, { T, slotLabel: (c) => D.slotLabels?.[c || ''] || c, prefLabel: J.prefLabel(D.prefNames) });
      setText('[data-receipt="date"]', v.date);
      setText('[data-receipt="time"]', v.time);
      setText('[data-receipt="place"]', v.place);
      // Osaka: the pick-up hours from the theme setting's slots (F-56)
      const slots = schedule().pickupSlots;
      const pickupText = J.fill(T.instruction_pickup, { first: slots[0] || '', last: slots[slots.length - 1] || '' });
      setText('[data-receipt="instruction"]', r.method === 'pickup' ? pickupText : r.stay === 'resident' ? T.instruction_home : T.instruction_hotel);
    }
    const missing = $('[data-receipt="missing"]');
    if (missing) {
      missing.hidden = !problem;
      missing.textContent = problem ? (r ? problem.message : T.receipt_missing) : '';
    }
    // stay dates to confirm (okurun revision 11)
    const dates = $('[data-stay-dates]');
    if (dates) {
      dates.hidden = !needsDates(r);
      if (needsDates(r)) {
        $('[data-stay-dates-value]').textContent = J.fill(T.stay_dates_value, { from: J.dateText(r.checkin, T), to: J.dateText(r.checkout, T), nights: J.fill(T.nights, { count: J.nights(r.checkin, r.checkout) }) });
        $('[data-stay-confirm]').checked = datesConfirmed(r);
      }
    }
    // carry
    const p = pens();
    const count = p.unknown ? undefined : J.carryCount({ destination: r?.destination || '', carryMode: 'order' }, p.total);
    // medicines without carry rules only (no carry group): 「持ち込み条件を確認」 and 「—」, the same as the receiving step
    const reviewOnly = !Object.values(D.cartVariants || {}).some((v) => v.group);
    const a = J.carryAssessment({ destination: r?.destination || '', carryMode: 'order' }, count, { rules, T, hub: D.hub, reviewOnly });
    setText('[data-carry="destination"]', a.destination || T.carry_unselected);
    setText('[data-carry="quantity"]', r?.destination && r.destination !== 'jp' ? a.quantity : '—');
    setText('[data-carry="status"]', a.status);
    // over (the quantities of the same medicine added up): the box, and only the country's own note under it
    const over = a.state === 'over';
    $$('.jwc-cart__block[data-step="carry"] [data-carry-over]').forEach((box) => {
      box.hidden = !over;
      const body = $('[data-carry-over-body]', box);
      if (body) body.textContent = over ? a.overBody : '';
    });
    setText('[data-carry="note"]', (over ? a.ruleNote : a.note) || '');
    const guide = $('[data-carry="guide"]');
    if (guide) {
      guide.hidden = !a.guide;
      guide.href = a.guide || D.hub;
    }
    // contact app
    const app = pendingApp ?? (cart.attributes?.['連絡アプリ'] || '');
    $$('input[name="contact-app"]').forEach((i) => (i.checked = i.value === app));
    // consents
    const need = required(r);
    $$('[data-consent-row="carry_guide"]').forEach((row) => (row.hidden = !need.includes('carry_guide')));
    setText('[data-consent-intro]', need.length === 3 ? T.consent_intro_3 : T.consent_intro_2);
    $$('[data-consent]').forEach((box) => (box.checked = checked[box.dataset.consent] === consentValue(box.dataset.consent)));
    markMissing(r, problem, need);
    // the certificate / cooler lines: quantity fixed, removed through 「変更」 (C03)
    lockExtras();
    // buttons
    const reason = blocker();
    $$('[data-jwc-checkout]').forEach((b) => {
      b.setAttribute('aria-disabled', String(Boolean(reason) || leaving));
      b.classList.toggle('jwc-is-blocked', Boolean(reason));
    });
    setText('[data-checkout-reminder]', reason && !updating ? reason.message : updating ? T.reminder_updating : '');
    const left = leftCount();
    setText('[data-checkout-left]', left ? J.fill(T.left, { n: left }) : T.ready);
    setText('[data-cart-total]', J.money(cart.total_price, D.moneyFormat));
    J.updateBars?.();
  }
  // After a tap on the blocked button (owner 2026-10-20): every step still missing turns red with its own line, the
  // unchecked boxes too; each goes back to normal as soon as it is done. The first one is scrolled to (go()).
  function markMissing(r, problem, need) {
    const on = tried && !updating && !leaving;
    const datesBad = Boolean(r) && needsDates(r) && !datesConfirmed(r);
    const bad = {
      receipt: on && (!r || Boolean(problem) || datesBad),
      carry: on && (!r || !Object.hasOwn(rules, r.destination)),
      contact: on && !(pendingApp ?? cart.attributes?.['連絡アプリ']),
      consents: on && need.some((id) => checked[id] !== consentValue(id)),
    };
    Object.entries(bad).forEach(([step, isBad]) => $$(`[data-step="${step}"]`).forEach((el) => el.classList.toggle('jwc-step--missing', isBad)));
    const line = (key, text) => $$(`[data-step-error="${key}"]`).forEach((p) => {
      p.hidden = !text;
      p.textContent = text || '';
    });
    line('dates', on && datesBad ? T.reminder_dates : '');
    line('carry', bad.carry ? T.reminder_carry : '');
    line('contact', bad.contact ? T.reminder_contact : '');
    line('consents', bad.consents ? (need.length === 3 ? T.reminder_consents_3 : T.reminder_consents_2) : '');
    $$('[data-stay-confirm]').forEach((i) => i.closest('.jwc-check')?.classList.toggle('jwc-check--missing', on && datesBad));
    $$('[data-consent]').forEach((box) => {
      const id = box.dataset.consent;
      box.closest('.jwc-check')?.classList.toggle('jwc-check--missing', on && need.includes(id) && checked[id] !== consentValue(id));
    });
    $$('[data-receipt="missing"]').forEach((p) => p.classList.toggle('jwc-cart__error', bad.receipt && Boolean(problem || !r)));
    $$('[data-checkout-reminder]').forEach((p) => p.classList.toggle('jwc-checkout-reminder--missing', on && Boolean(blocker())));
  }
  function lockExtras() {
    $$('#main-cart-items [data-quantity-variant-id]').forEach((input) => {
      const id = Number(input.dataset.quantityVariantId);
      if (id !== certId && !coolerIds.includes(id)) return;
      const row = input.closest('.cart-item');
      if (!row || row.dataset.jwcLocked) return;
      row.dataset.jwcLocked = '1';
      row.querySelectorAll('quantity-input, cart-remove-button').forEach((el) => (el.hidden = true));
      const wrapper = input.closest('.cart-item__quantity-wrapper') || input.closest('td') || row;
      const note = document.createElement('p');
      note.className = 'caption jwc-cart__extra-note';
      note.textContent = `× ${input.value || 1}　${T.cert_note}`;
      wrapper.appendChild(note);
      // the certificate: its promise and 「条件を見る →」 under the line (plan certificate §4-3)
      if (id === certId && certGate()) {
        const box = document.createElement('div');
        box.className = 'jwc-cart__cert';
        const usp = document.createElement('p');
        usp.className = 'jwc-cart__cert-usp';
        usp.textContent = cfg.cert.usp;
        const link = document.createElement('a');
        link.className = 'jwc-cart__cert-link';
        const u = new URL(cfg.cert.url, location.href);
        if (!cfg.cert.live) u.searchParams.set('view', 'certificate'); // checking before the page is switched on
        link.href = u.href;
        link.textContent = cfg.cert.link;
        box.append(usp, link);
        (row.querySelector('.cart-item__details') || wrapper).appendChild(box);
      }
    });
  }
  function certGate() {
    if (!cfg.cert) return false;
    if (cfg.cert.live) return true;
    try {
      return sessionStorage.getItem('jwc-cert-preview') === '1';
    } catch (e) {
      return false;
    }
  }

  // ---------------------------------------------------------------- refresh from the server cart
  async function refresh(fresh) {
    cart = fresh || (await J.fetchCart(cartUrl)) || cart;
    if (!cart) return;
    await tidy();
    digests = await computeDigests(receipt());
    restoreConsents();
    render();
  }
  // C03 / C04: one certificate with quantity 1; no certificate or cooler bag without medicine
  async function tidy() {
    const changes = [];
    const certs = cart.items.filter(isCert);
    const none = medicines().length === 0;
    certs.forEach((l, i) => {
      if (none || i > 0) changes.push({ id: l.key, quantity: 0 });
      else if (l.quantity !== 1) changes.push({ id: l.key, quantity: 1 });
    });
    if (none) cart.items.filter(isCooler).forEach((l) => changes.push({ id: l.key, quantity: 0 }));
    if (!changes.length) return;
    for (const c of changes) {
      await J.request(`${routes.cart_change_url || '/cart/change'}.js`, c);
      await J.sleep(300);
    }
    // the page shows the cart as it is now
    location.reload();
  }

  // ---------------------------------------------------------------- Dawn's redraw after a quantity change (C02)
  // Dawn waits 300ms after a quantity change, then disables the lines (cart__items--disabled) while it saves and redraws.
  // The buttons close at the first touch of a quantity and open again only after the redraw and a fresh check.
  const items = document.getElementById('main-cart-items');
  let fallback = null;
  let seenBusy = false;
  function finishUpdate() {
    clearTimeout(fallback);
    queue(async () => {
      await J.sleep(200);
      await refresh();
      updating = false;
      seenBusy = false;
      render();
    });
  }
  function markUpdating() {
    if (!updating) {
      updating = true;
      render();
    }
    clearTimeout(fallback);
    // nothing changed after all (the same quantity): open again after a while
    fallback = setTimeout(() => !seenBusy && finishUpdate(), 2500);
  }
  if (items) {
    items.addEventListener('change', (e) => e.target.closest('quantity-input, .quantity') && markUpdating(), true);
    items.addEventListener('input', (e) => e.target.closest('quantity-input, .quantity') && markUpdating(), true);
    items.addEventListener('click', (e) => e.target.closest('quantity-input button, cart-remove-button') && markUpdating(), true);
    new MutationObserver(() => {
      const busy = items.classList.contains('cart__items--disabled');
      if (busy) {
        seenBusy = true;
        clearTimeout(fallback);
        if (!updating) {
          updating = true;
          render();
        }
      } else if (updating && seenBusy) {
        // the redraw is over: check everything again before the buttons open
        finishUpdate();
      }
    }).observe(items, { attributes: true, attributeFilter: ['class'] });
  }

  // ---------------------------------------------------------------- events
  document.addEventListener('change', (e) => {
    const t = e.target;
    if (t.matches('[data-consent]')) {
      if (t.checked) checked[t.dataset.consent] = consentValue(t.dataset.consent);
      else delete checked[t.dataset.consent];
      writeDraft(t.dataset.consent, t.checked ? consentValue(t.dataset.consent) : '');
      render();
    } else if (t.matches('[data-stay-confirm]')) {
      try {
        if (t.checked) sessionStorage.setItem(DATES_KEY, `${cart?.token}|${datesKey(receipt())}`);
        else sessionStorage.removeItem(DATES_KEY);
      } catch (err) {
        /* the box stays as it is on screen only */
      }
      render();
    } else if (t.matches('input[name="contact-app"]')) {
      const app = t.value;
      pendingApp = app;
      queue(async () => {
        const after = await J.saveAttributes(attrRoutes, { 連絡アプリ: app });
        const msg = $('[data-contact-message]');
        pendingApp = null;
        if (after) cart = after;
        if (msg) {
          msg.hidden = Boolean(after);
          msg.textContent = after ? '' : T.contact_failed;
        }
        render();
      });
    }
  });

  // the receiving form in a drawer (「変更」)
  const drawer = document.getElementById('JwcReceiveDrawer');
  if (drawer && drawer.parentElement !== document.body) document.body.appendChild(drawer);
  function openDrawer(part) {
    if (!drawer) return;
    drawer.classList.add('active');
    document.body.classList.add('overflow-hidden');
    J.openReceive?.();
    setTimeout(() => {
      const target = part === 'carry' ? drawer.querySelector('[id$="-carry"]') : drawer.querySelector('input, select');
      target?.scrollIntoView?.({ block: 'start' });
      drawer.querySelector('.drawer__inner')?.focus();
    }, 200);
  }
  function closeDrawer() {
    drawer?.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
  }
  document.addEventListener('click', (e) => {
    const open = e.target.closest('[data-open-receive]');
    if (open) {
      e.preventDefault();
      openDrawer(open.dataset.openReceive);
      return;
    }
    if (e.target.closest('[data-close-receive]') || e.target === drawer) closeDrawer();
  });
  document.addEventListener('keyup', (e) => e.key === 'Escape' && closeDrawer());
  // from the certificate's page (「診断書を追加する」 with medicine in the cart): the drawer, where it is ticked
  // (assets/jwc-receive.js, jwc-cert-jump)
  if (new URLSearchParams(location.search).get('jwc') === 'certificate') {
    const url = new URL(location.href);
    url.searchParams.delete('jwc');
    history.replaceState(history.state, '', url);
    openDrawer('certificate');
  }

  // checkout: capture the cart form's submit (page button, phone bar, Enter) before Dawn
  const form = document.getElementById('cart');
  const go = (event) => {
    if (form?.dataset.jwcGo === '1') return; // our own submit after saving
    event?.preventDefault();
    event?.stopImmediatePropagation?.();
    const reason = blocker();
    if (reason || leaving) {
      if (reason && !updating) tried = true;
      render();
      setText('[data-checkout-reminder]', reason ? reason.message : '');
      const step = document.querySelector(`[data-step="${reason?.step}"]`);
      if (step) {
        step.scrollIntoView({ block: 'center', behavior: 'smooth' });
        step.classList.remove('jwc-flash');
        void step.offsetWidth;
        step.classList.add('jwc-flash');
      }
      return;
    }
    queue(checkout);
  };
  document.addEventListener('submit', (e) => e.target === form && go(e), true);
  document.addEventListener('click', (e) => {
    const b = e.target.closest('[data-jwc-checkout]');
    if (!b) return;
    if (b.form === form && b.type === 'submit') return; // handled by the submit listener
    e.preventDefault();
    go(null);
  });

  async function checkout() {
    leaving = true;
    render();
    setText('[data-checkout-reminder]', T.checkout_saving);
    try {
      const fresh = await J.fetchCart(cartUrl);
      if (!fresh) throw new Error('cart');
      cart = fresh;
      digests = await computeDigests(receipt());
      // the content may have changed in another tab: the boxes must still cover it
      const need = required(receipt());
      if (need.some((id) => checked[id] !== consentValue(id)) || blocker()) {
        leaving = false;
        restoreConsents();
        render();
        return;
      }
      const acceptedAt = new Date().toISOString();
      const r = receipt();
      const record = {
        schema_version: 1,
        accepted_at: acceptedAt,
        locale: cfg.locale,
        items: [
          ...need.map((id) => ({ id, version: cfg.consentVersions[id], relevant_context_digest: id === 'terms_privacy' ? null : digests[id] })),
          ...(needsDates(r) ? [{ id: 'stay_dates', version: '1', relevant_context_digest: datesKey(r) }] : []),
        ],
      };
      const after = await J.saveAttributes(attrRoutes, {
        同意: `${need.map((id) => LEGACY[id]).join('／')}（${acceptedAt}）`,
        jwc_consent_receipt_v1: JSON.stringify(record),
      });
      if (!after) throw new Error('save');
      cart = after;
      let prefill = null;
      try {
        prefill = JSON.parse(localStorage.getItem('jwc-checkout-prefill') || 'null');
      } catch (e) {
        /* checkout starts from the attributes only */
      }
      const pre = await J.preselectDelivery(D.store, cart, r, prefill); // best effort (C09); F-66 names, hotel, phone
      // another hotel's (or an older) street is still on the cart because replacing it failed: not to checkout with it
      if (pre?.blocking) throw new Error('address');
      // for the banner when they come back after the order (assets/jwc-start.js; owner 2026-10-22)
      try {
        localStorage.setItem('jwc-after-checkout', JSON.stringify({ app: cart.attributes?.['連絡アプリ'] || '', at: Date.now() }));
      } catch (e) {
        /* no banner later */
      }
      form.dataset.jwcGo = '1';
      const button = document.getElementById('checkout');
      if (form.requestSubmit && button) form.requestSubmit(button);
      else location.assign('/checkout');
    } catch (e) {
      leaving = false;
      setText('[data-checkout-reminder]', T.checkout_failed);
      render();
    }
  }

  // coming back from checkout with the back button can show a stored copy of this page
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) {
      leaving = false;
      if (form) delete form.dataset.jwcGo;
      queue(() => refresh());
    }
  });

  const bar = document.querySelector('[data-jwc-checkout-bar]');
  if (bar && bar.parentElement !== document.body) document.body.appendChild(bar);
  if (bar) bar.hidden = false;
  queue(() => refresh());
})();
